import {
  buildMaxExposureByOutputMonth,
  buildMaxMonthlyDrawByOutputMonth,
  parseOptionalLimit,
  planHasAnyExposureLimit,
  resolveRequiredMaxExposure,
  planHasAnyMonthlyDrawLimit,
  resolveMaxExposureAtOutputMonth,
  shouldApplyDrawLimits,
} from './resolvePlanCovenantLimits';
import { isMarketingPlanManualMode, stateHasResolvedDraws } from './resolvePlanFunding';
import { isExposureOverLimit } from './exposureGradeLimits';
import { computeFacilityLimitViolations } from './facilityLimitViolations';
import { fmtExposurePct, fmtMoney } from './formatters';

export function extractConstraintLimits(state, exposureGradeLimits) {
  const applyDrawLimits = shouldApplyDrawLimits(state);
  const manualMode = isMarketingPlanManualMode(state);
  return {
    maxExposure: manualMode
      ? null
      : resolveRequiredMaxExposure(state?.maxExposure),
    maxMonthlyDraw: applyDrawLimits
      ? parseOptionalLimit(state?.maxMonthlyDraw)
      : null,
    maxDrawIncreasePct: applyDrawLimits
      ? parseOptionalLimit(state?.maxDrawIncreasePct)
      : null,
    runRateMaxPct:
      exposureGradeLimits?.runRateMaxPct != null
        ? Number(exposureGradeLimits.runRateMaxPct)
        : null,
    runoffMaxPct:
      exposureGradeLimits?.runoffMaxPct != null
        ? Number(exposureGradeLimits.runoffMaxPct)
        : null,
  };
}

export function hasActiveConstraints(state, exposureGradeLimits) {
  if (isMarketingPlanManualMode(state)) {
    const limits = extractConstraintLimits(state, exposureGradeLimits);
    return (
      planHasAnyExposureLimit(state) ||
      limits.runRateMaxPct != null ||
      limits.runoffMaxPct != null
    );
  }

  const limits = extractConstraintLimits(state, exposureGradeLimits);
  return (
    limits.maxExposure != null ||
    limits.maxMonthlyDraw != null ||
    planHasAnyExposureLimit(state) ||
    planHasAnyMonthlyDrawLimit(state) ||
    limits.maxDrawIncreasePct != null ||
    limits.runRateMaxPct != null ||
    limits.runoffMaxPct != null
  );
}

/** True when auto-fix can adjust funding for facility covenants (excludes grade limits). */
export function hasActiveFacilityConstraints(state) {
  if (isMarketingPlanManualMode(state)) {
    return planHasAnyExposureLimit(state);
  }

  const limits = extractConstraintLimits(state, null);
  return (
    limits.maxExposure != null ||
    limits.maxMonthlyDraw != null ||
    planHasAnyExposureLimit(state) ||
    planHasAnyMonthlyDrawLimit(state) ||
    limits.maxDrawIncreasePct != null
  );
}

export function stateHasDraws(state) {
  return stateHasResolvedDraws(state);
}

/** Last output month where run rate / runoff grade limits apply for fixing. */
export function resolveLastGradeExposureCheckMonth(calcResult) {
  const lastDrawMonth = calcResult?.lastDrawMonth ?? -1;
  return lastDrawMonth >= 0 ? lastDrawMonth : -1;
}

function buildFacilityLimitInputs(state, calcResult, limits) {
  const outputMonthCount = Math.max(
    calcResult?.draw?.length ?? 0,
    calcResult?.endBal?.length ?? 0
  );
  return {
    draw: calcResult?.draw ?? [],
    endBal: calcResult?.endBal ?? [],
    maxExposure: limits.maxExposure,
    maxMonthlyDraw: limits.maxMonthlyDraw,
    maxExposureByMonth: buildMaxExposureByOutputMonth(state, outputMonthCount),
    maxMonthlyDrawByMonth: buildMaxMonthlyDrawByOutputMonth(
      state,
      outputMonthCount
    ),
    maxDrawIncreasePct: limits.maxDrawIncreasePct,
  };
}

function calcHasGradeExposureViolations(calcResult, limits) {
  const lastGradeExposureCheckMonth = resolveLastGradeExposureCheckMonth(calcResult);

  if (limits.runRateMaxPct != null && lastGradeExposureCheckMonth >= 0) {
    for (let t = 0; t <= lastGradeExposureCheckMonth; t += 1) {
      const value = calcResult.runrateExposurePct?.[t];
      if (isExposureOverLimit(value, limits.runRateMaxPct)) {
        return true;
      }
    }
  }

  if (limits.runoffMaxPct != null && lastGradeExposureCheckMonth >= 0) {
    for (let t = 0; t <= lastGradeExposureCheckMonth; t += 1) {
      const value = calcResult.runoffExposurePct?.[t];
      if (isExposureOverLimit(value, limits.runoffMaxPct)) {
        return true;
      }
    }
  }

  return false;
}

export function calcHasFacilityViolations(state, calcResult) {
  if (!calcResult) {
    return false;
  }
  const limits = extractConstraintLimits(state, null);
  const { drawViolations, endBalViolations } = computeFacilityLimitViolations(
    buildFacilityLimitInputs(state, calcResult, limits)
  );

  return drawViolations.some(Boolean) || endBalViolations.some(Boolean);
}

export function calcHasViolations(state, calcResult, exposureGradeLimits) {
  if (!calcResult) {
    return false;
  }
  if (calcHasFacilityViolations(state, calcResult)) {
    return true;
  }

  const limits = extractConstraintLimits(state, exposureGradeLimits);
  return calcHasGradeExposureViolations(calcResult, limits);
}

const VIOLATION_WORSENING_TOLERANCE_PCT = 0.01;

/**
 * Snapshot of every currently-violated constraint (draw, ending balance, runrate
 * exposure, runoff exposure), keyed by `"type:outputMonth"`, mapped to the
 * violating value. Used to tell apart violations actually caused by the plan
 * month being adjusted from pre-existing violations driven by other cohorts.
 */
export function getConstraintMetricSnapshot(state, calcResult, exposureGradeLimits) {
  const snapshot = new Map();
  if (!calcResult) {
    return snapshot;
  }
  const limits = extractConstraintLimits(state, exposureGradeLimits);
  const { drawViolations, endBalViolations } = computeFacilityLimitViolations(
    buildFacilityLimitInputs(state, calcResult, limits)
  );

  drawViolations.forEach((violated, i) => {
    if (violated) snapshot.set(`draw:${i}`, Number(calcResult.draw?.[i]) || 0);
  });
  endBalViolations.forEach((violated, i) => {
    if (violated) snapshot.set(`endBal:${i}`, Number(calcResult.endBal?.[i]) || 0);
  });

  const lastGradeExposureCheckMonth = resolveLastGradeExposureCheckMonth(calcResult);

  if (limits.runRateMaxPct != null && lastGradeExposureCheckMonth >= 0) {
    for (let i = 0; i <= lastGradeExposureCheckMonth; i += 1) {
      const value = calcResult.runrateExposurePct?.[i];
      if (isExposureOverLimit(value, limits.runRateMaxPct)) {
        snapshot.set(`runrate:${i}`, value);
      }
    }
  }
  if (limits.runoffMaxPct != null && lastGradeExposureCheckMonth >= 0) {
    for (let i = 0; i <= lastGradeExposureCheckMonth; i += 1) {
      const value = calcResult.runoffExposurePct?.[i];
      if (isExposureOverLimit(value, limits.runoffMaxPct)) {
        snapshot.set(`runoff:${i}`, value);
      }
    }
  }

  return snapshot;
}

/** Facility covenant snapshot only (draw + ending balance). Used by auto-fix. */
export function getFacilityConstraintMetricSnapshot(state, calcResult) {
  const snapshot = new Map();
  if (!calcResult) {
    return snapshot;
  }
  const limits = extractConstraintLimits(state, null);
  const { drawViolations, endBalViolations } = computeFacilityLimitViolations(
    buildFacilityLimitInputs(state, calcResult, limits)
  );

  drawViolations.forEach((violated, i) => {
    if (violated) snapshot.set(`draw:${i}`, Number(calcResult.draw?.[i]) || 0);
  });
  endBalViolations.forEach((violated, i) => {
    if (violated) snapshot.set(`endBal:${i}`, Number(calcResult.endBal?.[i]) || 0);
  });

  return snapshot;
}

/**
 * True if `candidateSnapshot` contains a violation that either isn't present in
 * `baselineSnapshot` at all, or is meaningfully worse than it was at baseline.
 * Violations present in the baseline at essentially the same magnitude are
 * treated as unrelated to the change being evaluated (e.g. caused by other,
 * untouched cohorts) rather than something the candidate must resolve.
 */
export function hasNewOrWorseViolations(baselineSnapshot, candidateSnapshot) {
  for (const [key, value] of candidateSnapshot) {
    const baselineValue = baselineSnapshot.get(key);
    if (baselineValue == null || value > baselineValue + VIOLATION_WORSENING_TOLERANCE_PCT) {
      return true;
    }
  }
  return false;
}

function monthLabel(cols, index) {
  return cols?.[index] ?? `Month ${index + 1}`;
}

export function summarizeConstraintViolations(state, calcResult, exposureGradeLimits) {
  const limits = extractConstraintLimits(state, exposureGradeLimits);
  const { drawViolations, endBalViolations } = computeFacilityLimitViolations(
    buildFacilityLimitInputs(state, calcResult, limits)
  );

  const cols = calcResult?.cols ?? [];
  const summaries = [];

  for (let t = 0; t < (calcResult?.draw?.length ?? 0); t += 1) {
    if (drawViolations[t]) {
      summaries.push(`Draw constraint exceeded in ${monthLabel(cols, t)}`);
    }
  }

  for (let t = 0; t < endBalViolations.length; t += 1) {
    if (endBalViolations[t]) {
      const limit = resolveMaxExposureAtOutputMonth(state, t);
      const endBal = Number(calcResult?.endBal?.[t]) || 0;
      if (limit != null && endBal > limit) {
        summaries.push(
          `Ending balance ${fmtMoney(endBal)} exceeds max exposure ${fmtMoney(limit)} in ${monthLabel(cols, t)}`
        );
      } else {
        summaries.push(`Max exposure exceeded in ${monthLabel(cols, t)}`);
      }
    }
  }

  const lastGradeExposureCheckMonth = resolveLastGradeExposureCheckMonth(calcResult);

  if (limits.runRateMaxPct != null && lastGradeExposureCheckMonth >= 0) {
    for (let t = 0; t <= lastGradeExposureCheckMonth; t += 1) {
      const value = calcResult.runrateExposurePct?.[t];
      if (isExposureOverLimit(value, limits.runRateMaxPct)) {
        summaries.push(
          `Runrate exposure ${fmtExposurePct(value)} above grade limit ${fmtExposurePct(limits.runRateMaxPct)} in ${monthLabel(cols, t)}`
        );
      }
    }
  }

  if (limits.runoffMaxPct != null && lastGradeExposureCheckMonth >= 0) {
    for (let t = 0; t <= lastGradeExposureCheckMonth; t += 1) {
      const value = calcResult.runoffExposurePct?.[t];
      if (isExposureOverLimit(value, limits.runoffMaxPct)) {
        summaries.push(
          `Runoff exposure ${fmtExposurePct(value)} above grade limit ${fmtExposurePct(limits.runoffMaxPct)} in ${monthLabel(cols, t)}`
        );
      }
    }
  }

  return {
    hasViolations: summaries.length > 0,
    summaries: [...new Set(summaries)],
    limits,
  };
}
